import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";

const BASE = "/v1/puzzle/jigsaw";
const HOME_JIGSAW_BASE = "/home/jigsaw";
const COLOR_BASKET_PLAY_BASE = "/v1/puzzle/jigsaw-color-basket-play";
const PACK_BASE = "/v1/puzzle/jigsaw/pack";

const MAX_BATCH_SIZE = 1000;

function clampBatchSize(size, fallback = 10) {
    const n = Number(size);
    return Number.isFinite(n) ? Math.min(MAX_BATCH_SIZE, Math.max(1, Math.round(n))) : fallback;
}

function clampEnsureBatchSize(size, fallback = 10) {
    return clampBatchSize(size, fallback);
}

function parseBatchOpResponse(response) {
    if (response && response.code === "FAILURE") {
        return {
            success: false,
            error: response.message || "Operation failed",
            data: null,
        };
    }
    return {
        success: true,
        data: {
            candidates: response?.candidates ?? response?.data?.candidates ?? undefined,
            processed: response?.processed ?? response?.data?.processed ?? undefined,
            results: response?.results ?? response?.data?.results ?? [],
            errors: response?.errors ?? response?.data?.errors ?? [],
            raw: response,
        },
    };
}

function extractDistinctTagsList(response) {
    if (response == null) return [];
    if (Array.isArray(response)) return response;
    if (typeof response !== "object") return [];
    if (response.code === "FAILURE") return null;

    const direct = [
        response.data,
        response.content,
        response.items,
        response.tags,
        response.result,
        response.payload,
    ];
    for (const val of direct) {
        if (Array.isArray(val)) return val;
    }
    if (response.data && typeof response.data === "object") {
        for (const val of [response.data.content, response.data.items, response.data.tags]) {
            if (Array.isArray(val)) return val;
        }
    }
    return [];
}

function normalizeDistinctTagRows(raw) {
    const list = extractDistinctTagsList(raw);
    if (list === null) return null;
    const seen = new Set();
    const out = [];
    for (const item of list) {
        let tag = "";
        let count;
        if (typeof item === "string") {
            tag = item.trim();
        } else if (item && typeof item === "object") {
            tag = String(item.tag ?? item.name ?? item.label ?? item.value ?? "").trim();
            const n = item.count;
            count = typeof n === "number" && !Number.isNaN(n) ? n : undefined;
        }
        if (!tag || tag.toLowerCase() === "bedrock") continue;
        const key = tag.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ tag, count });
    }
    return out;
}

function normalizeStoryLanguage(story) {
    const raw = story?.language;
    if (typeof raw !== "string") return "";
    return raw.trim().toLowerCase();
}

function groupMatchesLanguageFilter(group, language) {
    const lang = typeof language === "string" ? language.trim().toLowerCase() : "";
    if (!lang) return true;
    return normalizeStoryLanguage(group?.story) === lang;
}

function groupMatchesTagsFilter(group, tags) {
    if (!Array.isArray(tags) || tags.length === 0) return true;
    const story = group?.story;
    const tagPool = [];
    const pushTags = (rawTags) => {
        if (!Array.isArray(rawTags)) return;
        for (const t of rawTags) {
            const s = String(t ?? "").trim().toLowerCase();
            if (s) tagPool.push(s);
        }
    };
    pushTags(group?.puzzleTemplate?.tags ?? group?.puzzle_template?.tags);
    pushTags(story?.tags);
    const genre = story?.topic?.genre?.name;
    if (typeof genre === "string" && genre.trim()) {
        tagPool.push(genre.trim().toLowerCase());
    }
    if (tagPool.length === 0) return false;
    const wanted = tags.map((t) => String(t ?? "").trim().toLowerCase()).filter(Boolean);
    return wanted.some((t) => tagPool.includes(t));
}

function pushLocaleSearchText(parts, loc) {
    if (!loc || typeof loc !== "object") return;
    const title = String(loc.title ?? "").trim();
    const name = String(loc.name ?? "").trim();
    if (title) parts.push(title.toLowerCase());
    if (name) parts.push(name.toLowerCase());
}

function groupMatchesKeyword(group, keyword) {
    const kw = typeof keyword === "string" ? keyword.trim().toLowerCase() : "";
    if (!kw) return true;
    const parts = [];
    const story = group?.story;
    if (story) {
        pushLocaleSearchText(parts, story.english);
        pushLocaleSearchText(parts, story.en);
        pushLocaleSearchText(parts, story.french);
        pushLocaleSearchText(parts, story.fr);
        const locales = story.stories;
        if (locales && typeof locales === "object") {
            Object.values(locales).forEach((loc) => pushLocaleSearchText(parts, loc));
        }
        const title = String(story.title ?? "").trim();
        const name = String(story.name ?? "").trim();
        if (title) parts.push(title.toLowerCase());
        if (name) parts.push(name.toLowerCase());
        pushLocaleSearchText(parts, story.crossTpl);
        pushLocaleSearchText(parts, story.gridTpl);
        pushLocaleSearchText(parts, story.crosswordTpl);
    }
    const puzzleTpl = group?.puzzleTemplate ?? group?.puzzle_template;
    pushLocaleSearchText(parts, puzzleTpl);
    return parts.some((text) => text.includes(kw));
}

const AdminJigsawService = {
    /** GET /home/jigsaw/distinct-tags — `{ tag, count }[]` for linked template tags */
    getDistinctTags: async (state, language) => {
        try {
            const params = {};
            const stateTrim = typeof state === "string" ? state.trim() : "";
            if (stateTrim) params.state = stateTrim;
            const langTrim = typeof language === "string" ? language.trim() : "";
            if (langTrim) params.language = langTrim;
            const response = await ApiService.getData(`${HOME_JIGSAW_BASE}/distinct-tags`, params);
            const normalized = normalizeDistinctTagRows(response);
            if (normalized === null) {
                return {
                    success: false,
                    error: response?.message || "Failed to load distinct tags",
                    data: [],
                };
            }
            normalized.sort((a, b) =>
                a.tag.localeCompare(b.tag, undefined, { sensitivity: "base" }),
            );
            return { success: true, data: normalized };
        } catch (e) {
            Logger.error("AdminJigsawService.getDistinctTags:", e);
            return { success: false, error: e.message || "Failed to load distinct tags", data: [] };
        }
    },

    /** GET /v1/puzzle/jigsaw — paginated list of JigsawStoryGroupRo */
    list: async (page = 0, pageSize = 20, sort = "publishedAt:desc", state, keyword, language, tags, free, standaloneImage, packed) => {
        try {
            const params = { page, pageSize, sort };
            if (state) params.state = state;
            if (free === true || free === false) params.free = free ? "true" : "false";
            if (packed === true || packed === false) params.packed = packed ? "true" : "false";
            if (standaloneImage === true || standaloneImage === false) {
                params.standaloneImage = standaloneImage ? "true" : "false";
            }
            const kw = typeof keyword === "string" ? keyword.trim() : "";
            if (kw) params.keyword = encodeURIComponent(kw);
            const lang = typeof language === "string" ? language.trim() : "";
            if (lang) params.language = lang;
            if (tags && Array.isArray(tags) && tags.length > 0) {
                const tagStrings = tags
                    .map((tag) => {
                        const s = typeof tag === "string" ? tag : tag?.tag ?? String(tag ?? "");
                        return s.trim().toLowerCase();
                    })
                    .filter((t) => t.length > 0);
                if (tagStrings.length > 0) {
                    params.tags = tagStrings.map((t) => encodeURIComponent(t)).join(",");
                }
            }
            const response = await ApiService.getData(BASE, params);
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            const pageBlock = response?.data && typeof response.data === "object" && !Array.isArray(response.data)
                ? response.data
                : response;
            const pageMeta = response?.page ?? pageBlock?.page ?? null;
            let content = pageBlock?.content ?? response?.content ?? [];
            content = Array.isArray(content) ? content : [];
            if (kw) {
                content = content.filter((group) => groupMatchesKeyword(group, kw));
            }
            if (tags && Array.isArray(tags) && tags.length > 0) {
                const tagStrings = tags
                    .map((tag) => {
                        const s = typeof tag === "string" ? tag : tag?.tag ?? String(tag ?? "");
                        return s.trim().toLowerCase();
                    })
                    .filter((t) => t.length > 0);
                if (tagStrings.length > 0) {
                    content = content.filter((group) => groupMatchesTagsFilter(group, tagStrings));
                }
            }
            const toInt = (value) => {
                if (value == null || value === "") return null;
                const n = typeof value === "string" ? parseInt(value, 10) : Number(value);
                return Number.isFinite(n) ? n : null;
            };
            const apiTotalElements = toInt(
                pageMeta?.totalElements
                ?? pageMeta?.total_elements
                ?? pageBlock?.totalElements
                ?? pageBlock?.total_elements
                ?? response?.totalElements,
            );
            const apiTotalPages = toInt(
                pageMeta?.totalPages
                ?? pageMeta?.total_pages
                ?? pageBlock?.totalPages
                ?? pageBlock?.total_pages
                ?? response?.totalPages,
            );
            const totalElements = apiTotalElements != null ? apiTotalElements : content.length;
            let totalPages = apiTotalPages != null && apiTotalPages >= 0 ? apiTotalPages : null;
            if ((totalPages == null || totalPages < 1) && apiTotalElements != null && apiTotalElements > 0) {
                totalPages = Math.max(1, Math.ceil(apiTotalElements / pageSize));
            }
            return {
                success: true,
                data: {
                    content,
                    totalElements,
                    totalPages: totalPages == null ? 0 : totalPages,
                },
            };
        } catch (e) {
            Logger.error("AdminJigsawService.list:", e);
            return { success: false, error: e.message };
        }
    },

    /** GET /v1/puzzle/jigsaw/{storyId}/story */
    getGroupByStoryId: async (storyId) => {
        try {
            if (!storyId) return { success: false, error: "Story id required" };
            const response = await ApiService.getData(`${BASE}/${encodeURIComponent(storyId)}/story`);
            if (response?.code === "NO_DATA") return { success: false, code: "NO_DATA", error: response.message };
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.getGroupByStoryId:", e);
            return { success: false, error: e.message };
        }
    },

    /** GET /v1/puzzle/jigsaw/{id} — single template */
    getById: async (id) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const response = await ApiService.getData(`${BASE}/${encodeURIComponent(id)}`);
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.getById:", e);
            return { success: false, error: e.message };
        }
    },

    /** POST /v1/puzzle/jigsaw — create template */
    create: async ({ storyId, difficultyLevel, pieceCount, svgPath, piecePaths }) => {
        try {
            const body = { storyId, svgPath, piecePaths };
            if (difficultyLevel) body.difficultyLevel = difficultyLevel;
            if (pieceCount) body.pieceCount = pieceCount;
            const response = await ApiService.postData(BASE, body);
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.create:", e);
            return { success: false, error: e.message };
        }
    },

    /** PUT /v1/puzzle/jigsaw?makeWeakAgo=false — update template */
    update: async (fields = {}, makeWeakAgo = false) => {
        try {
            const { id } = fields;
            if (!id) return { success: false, error: "Id required" };
            const weak = makeWeakAgo ? "true" : "false";
            const response = await ApiService.putData(`${BASE}?makeWeakAgo=${weak}`, fields);
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.update:", e);
            return { success: false, error: e.message };
        }
    },

    /** GET /v1/puzzle/jigsaw/pack/user/{cloudUserId}/grants — packs this player bought in the app or on the web. */
    listUserPackGrants: async (cloudUserId) => {
        try {
            if (!cloudUserId) return { success: false, error: "User id required", data: null };
            const response = await ApiService.getData(
                `${PACK_BASE}/user/${encodeURIComponent(cloudUserId)}/grants`
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to load pack purchases", data: null };
            }
            const root = response?.grants ? response : response?.data ?? response;
            return { success: true, data: { count: root?.count ?? 0, grants: root?.grants ?? [] } };
        } catch (e) {
            Logger.error("AdminJigsawService.listUserPackGrants:", e);
            return { success: false, error: e.message || "Failed to load pack purchases", data: null };
        }
    },

    /** GET /v1/puzzle/jigsaw-color-basket-play/{cloudUserId}/plays — one player's color basket sessions. */
    listUserColorBasketPlays: async (cloudUserId) => {
        try {
            if (!cloudUserId) return { success: false, error: "User id required", data: null };
            const response = await ApiService.getData(
                `${COLOR_BASKET_PLAY_BASE}/${encodeURIComponent(cloudUserId)}/plays`
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to load color basket plays", data: null };
            }
            const root = response?.plays ? response : response?.data ?? response;
            return { success: true, data: { count: root?.count ?? 0, plays: root?.plays ?? [] } };
        } catch (e) {
            Logger.error("AdminJigsawService.listUserColorBasketPlays:", e);
            return { success: false, error: e.message || "Failed to load color basket plays", data: null };
        }
    },

    /** DELETE /v1/puzzle/jigsaw-color-basket-play/{cloudUserId}/plays — wipe every session of a player. */
    deleteUserColorBasketPlays: async (cloudUserId) => {
        try {
            if (!cloudUserId) return { success: false, error: "User id required", data: null };
            const response = await ApiService.deleteData(
                `${COLOR_BASKET_PLAY_BASE}/${encodeURIComponent(cloudUserId)}/plays`
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to delete color basket plays", data: null };
            }
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.deleteUserColorBasketPlays:", e);
            return { success: false, error: e.message || "Failed to delete color basket plays", data: null };
        }
    },

    /** DELETE /v1/puzzle/jigsaw-color-basket-play/{cloudUserId}/plays/{playId} — one session. */
    deleteUserColorBasketPlay: async (cloudUserId, playId) => {
        try {
            if (!cloudUserId || !playId) return { success: false, error: "User id and play id required", data: null };
            const response = await ApiService.deleteData(
                `${COLOR_BASKET_PLAY_BASE}/${encodeURIComponent(cloudUserId)}/plays/${encodeURIComponent(playId)}`
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to delete color basket play", data: null };
            }
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.deleteUserColorBasketPlay:", e);
            return { success: false, error: e.message || "Failed to delete color basket play", data: null };
        }
    },

    /** DELETE /v1/puzzle/jigsaw/{id} */
    remove: async (id) => {
        try {
            if (!id) return { success: false, error: "Id required" };
            const response = await ApiService.deleteData(`${BASE}/${encodeURIComponent(id)}`);
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.remove:", e);
            return { success: false, error: e.message };
        }
    },

    /** DELETE /v1/puzzle/jigsaw/{storyId}/story — story, sliding, jigsaw; crossword/grid template stays */
    removeByStoryId: async (storyId) => {
        try {
            if (!storyId) return { success: false, error: "Story id required" };
            const response = await ApiService.deleteData(`${BASE}/${encodeURIComponent(storyId)}/story`);
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.removeByStoryId:", e);
            return { success: false, error: e.message };
        }
    },

    /**
     * Publish one story bundle now (REVIEWED → PUBLISHED, published_at = now).
     * POST /v1/puzzle/jigsaw/{storyId}/publish
     */
    publishStory: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required" };
            }
            const response = await ApiService.postData(
                `${BASE}/${encodeURIComponent(storyId)}/publish`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Publish failed" };
            }
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.publishStory:", e);
            return { success: false, error: e.message || "Failed to publish jigsaw story" };
        }
    },

    /**
     * PUBLISHED → DISABLED. App cannot play it; discarded after 1 week.
     * PUT /v1/puzzle/jigsaw/{storyId}/story/disable
     */
    disableStory: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required" };
            }
            const response = await ApiService.putData(
                `${BASE}/${encodeURIComponent(storyId)}/story/disable`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Disable failed" };
            }
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.disableStory:", e);
            return { success: false, error: e.message || "Failed to disable jigsaw story" };
        }
    },

    /**
     * DRAFT → REVIEWED so the bundle is eligible for batch publish.
     * POST /v1/puzzle/jigsaw/{storyId}/mark-reviewed
     */
    markReviewed: async (storyId) => {
        try {
            if (!storyId) {
                return { success: false, error: "Story id required" };
            }
            const response = await ApiService.postData(
                `${BASE}/${encodeURIComponent(storyId)}/mark-reviewed`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Mark reviewed failed" };
            }
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.markReviewed:", e);
            return { success: false, error: e.message || "Failed to mark jigsaw story REVIEWED" };
        }
    },

    /** PUT /v1/puzzle/jigsaw/{storyId}/story/free with body { free: true|false } */
    setStoryFree: async (storyId, free) => {
        try {
            if (!storyId) return { success: false, error: "Story id required" };
            const flag = free === true;
            const response = await ApiService.putData(
                `${BASE}/${encodeURIComponent(storyId)}/story/free?free=${flag ? "true" : "false"}`,
                { free: flag }
            );
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            const saved = response?.storyJigsaw?.free ?? response?.data?.storyJigsaw?.free;
            if (typeof saved === "boolean" && saved !== flag) {
                return { success: false, error: "Free flag did not save", data: response };
            }
            return { success: true, data: response, free: typeof saved === "boolean" ? saved : flag };
        } catch (e) {
            Logger.error("AdminJigsawService.setStoryFree:", e);
            return { success: false, error: e.message };
        }
    },

    /** PUT /v1/puzzle/jigsaw/{storyId}/story/editors-picks?editorsPicks=true */
    setStoryEditorsPicks: async (storyId, editorsPicks = true) => {
        try {
            if (!storyId) return { success: false, error: "Story id required" };
            const response = await ApiService.putData(
                `${BASE}/${encodeURIComponent(storyId)}/story/editors-picks?editorsPicks=${editorsPicks}`,
                {},
            );
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.setStoryEditorsPicks:", e);
            return { success: false, error: e.message };
        }
    },

    /** PUT /v1/puzzle/jigsaw/{storyId}/story/tags — body `{ tags: string[] }` */
    setStoryTags: async (storyId, tags) => {
        try {
            if (!storyId) return { success: false, error: "Story id required" };
            const tagList = Array.isArray(tags)
                ? tags.map((tag) => {
                    const s = typeof tag === "string" ? tag : tag?.tag ?? String(tag ?? "");
                    return s.trim();
                }).filter((t) => t.length > 0)
                : [];
            const response = await ApiService.putData(
                `${BASE}/${encodeURIComponent(storyId)}/story/tags`,
                { tags: tagList },
            );
            if (response?.code === "FAILURE") return { success: false, error: response.message };
            return { success: true, data: response };
        } catch (e) {
            Logger.error("AdminJigsawService.setStoryTags:", e);
            return { success: false, error: e.message };
        }
    },

    /**
     * Batch-generate jigsaw templates for eligible stories without jigsaw rows yet.
     * POST /v1/puzzle/jigsaw/gen-jigsaw/null-story?size=…&makeWeakAgo=…
     * (size default 10, clamped 1–1000; makeWeakAgo default false)
     * Response: { candidates, processed, results: [{ storyId, templatesCreated }], errors: string[] }
     */
    /**
     * Batch-publish oldest REVIEWED jigsaw bundles.
     * POST /v1/puzzle/jigsaw/publish?batchSize=…&language=…
     */
    publishReviewed: async (batchSize = 10, language, tag) => {
        try {
            const clamped = clampBatchSize(batchSize);
            const params = new URLSearchParams();
            params.set("batchSize", String(clamped));
            const lang = language != null ? String(language).trim() : "";
            if (lang) params.set("language", lang);
            const tagTrim = tag != null ? String(tag).trim() : "";
            if (tagTrim) params.set("tag", tagTrim);
            const response = await ApiService.postData(
                `${BASE}/publish?${params.toString()}`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Publish failed", data: null };
            }
            return {
                success: true,
                data: {
                    requestedBatchSize:
                        response?.requestedBatchSize ?? response?.data?.requestedBatchSize ?? clamped,
                    publishedCount: response?.publishedCount ?? response?.data?.publishedCount ?? 0,
                    storyIds: response?.storyIds ?? response?.data?.storyIds ?? [],
                    raw: response,
                },
            };
        } catch (e) {
            Logger.error("AdminJigsawService.publishReviewed:", e);
            return { success: false, error: e.message || "Failed to publish jigsaw puzzles", data: null };
        }
    },

    /**
     * GET /v1/puzzle/jigsaw/publish/next?language=…&tag=…
     * The REVIEWED story that publish would take next for this tag.
     */
    peekPublishNext: async (language, tag) => {
        try {
            const params = {};
            const lang = language != null ? String(language).trim() : "";
            if (lang) params.language = lang;
            const tagTrim = tag != null ? String(tag).trim() : "";
            if (tagTrim) params.tag = tagTrim;
            const response = await ApiService.getData(`${BASE}/publish/next`, params);
            if (response?.code === "FAILURE" || response?.code === "NO_DATA") {
                return { success: false, error: response.message || "No REVIEWED jigsaw", data: null };
            }
            const group = response?.story ? response : (response?.data?.story ? response.data : null);
            if (!group) {
                return { success: false, error: "No REVIEWED jigsaw", data: null };
            }
            return { success: true, data: group };
        } catch (e) {
            Logger.error("AdminJigsawService.peekPublishNext:", e);
            return { success: false, error: e.message || "Failed to load next jigsaw", data: null };
        }
    },

    /**
     * POST /v1/puzzle/jigsaw/{storyId}/gen-preview
     * Response: list of JigsawPreviewGenItem { jigsawId, previewUrl, s3Key, difficultyLevel, tileSize }
     */
    genPreview: async (storyId) => {
        try {
            if (!storyId || typeof storyId !== "string" || !storyId.trim()) {
                return { success: false, error: "Story id required", data: null };
            }
            const response = await ApiService.postData(
                `${BASE}/${encodeURIComponent(storyId.trim())}/gen-preview`,
                {}
            );
            if (response && response.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Failed to generate preview",
                    data: null,
                };
            }
            const rawList = Array.isArray(response)
                ? response
                : Array.isArray(response?.items)
                  ? response.items
                  : Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.items)
                      ? response.data.items
                      : response?.previewUrl || response?.jigsawId
                        ? [response]
                        : [];
            const items = rawList
                .map((row) => {
                    if (!row || typeof row !== "object") return null;
                    const jigsawId = row.jigsawId ?? row.jigsaw_id ?? row.templateId ?? row.template_id ?? "";
                    const previewUrl = row.previewUrl ?? row.preview_url ?? "";
                    const s3Key = row.s3Key ?? row.s3_key ?? "";
                    const difficultyLevel = row.difficultyLevel ?? row.difficulty_level ?? "";
                    const tileSize = row.tileSize ?? row.tile_size ?? row.gridSize ?? row.grid_size ?? null;
                    return {
                        jigsawId: typeof jigsawId === "string" ? jigsawId.trim() : String(jigsawId ?? ""),
                        previewUrl: typeof previewUrl === "string" ? previewUrl.trim() : "",
                        s3Key: typeof s3Key === "string" ? s3Key.trim() : "",
                        difficultyLevel:
                            typeof difficultyLevel === "string"
                                ? difficultyLevel.trim()
                                : String(difficultyLevel ?? ""),
                        tileSize:
                            tileSize != null && Number.isFinite(Number(tileSize))
                                ? Number(tileSize)
                                : null,
                    };
                })
                .filter(Boolean);
            return { success: true, data: { items, raw: response } };
        } catch (error) {
            Logger.error("AdminJigsawService.genPreview:", error);
            return {
                success: false,
                error: error.message || "Failed to generate preview",
                data: null,
            };
        }
    },

    genJigsawNullStory: async (size = 10, makeWeakAgo = false) => {
        try {
            const clamped = clampBatchSize(size);
            const weak = makeWeakAgo === true ? "true" : "false";
            const response = await ApiService.postData(
                `/v1/puzzle/jigsaw/gen-jigsaw/null-story?size=${encodeURIComponent(clamped)}&makeWeakAgo=${weak}`,
                {}
            );
            return parseBatchOpResponse(response);
        } catch (error) {
            Logger.error("Error in genJigsawNullStory:", error);
            return {
                success: false,
                error: error.message || "Failed to generate jigsaw puzzles",
                data: null,
            };
        }
    },

    /**
     * POST /v1/puzzle/jigsaw/ensure?size=…
     * Backfill missing jigsaw rows for REVIEWED/PUBLISHED bundles vs enabled jigsaw_template rows.
     */
    ensureMissingTemplates: async (size = 10) => {
        try {
            const clamped = clampEnsureBatchSize(size);
            const response = await ApiService.postData(
                `${BASE}/ensure?size=${encodeURIComponent(clamped)}`,
                {}
            );
            return parseBatchOpResponse(response);
        } catch (error) {
            Logger.error("AdminJigsawService.ensureMissingTemplates:", error);
            return {
                success: false,
                error: error.message || "Failed to ensure jigsaw templates",
                data: null,
            };
        }
    },

    /**
     * POST /v1/puzzle/jigsaw/gen-pre-image/refresh?size=…&afterStoryId=… or run-all (?all=true, ?processAll=true, ?size=all)
     * Regenerate preview/share PNGs for stories that already have assets.
     */
    genPreImageRefresh: async (size = 10, { runAll = false, state, language, afterStoryId } = {}) => {
        try {
            const params = new URLSearchParams();
            if (runAll) {
                params.set("all", "true");
                params.set("processAll", "true");
                params.set("size", "all");
            } else {
                params.set("size", String(clampEnsureBatchSize(size)));
            }
            const stateTrim = state != null ? String(state).trim() : "";
            if (stateTrim) params.set("state", stateTrim);
            const langTrim = language != null ? String(language).trim() : "";
            if (langTrim) params.set("language", langTrim);
            const afterStoryIdTrim = afterStoryId != null ? String(afterStoryId).trim() : "";
            if (afterStoryIdTrim) params.set("afterStoryId", afterStoryIdTrim);
            const response = await ApiService.postData(
                `${BASE}/gen-pre-image/refresh?${params.toString()}`,
                {}
            );
            const parsed = parseBatchOpResponse(response);
            if (!parsed.success) return parsed;
            const root = response?.data && typeof response.data === "object" ? response.data : response;
            return {
                success: true,
                data: {
                    ...parsed.data,
                    all: root?.all ?? response?.all ?? undefined,
                    runAll: root?.runAll ?? response?.runAll ?? undefined,
                    sizeLimit:
                        root?.sizeLimit !== undefined
                            ? root.sizeLimit
                            : response?.sizeLimit !== undefined
                              ? response.sizeLimit
                              : undefined,
                    bundleStates:
                        root?.bundleStates ??
                        response?.bundleStates ??
                        undefined,
                    language: root?.language ?? response?.language ?? undefined,
                    requestedRunAll: runAll,
                },
            };
        } catch (error) {
            Logger.error("AdminJigsawService.genPreImageRefresh:", error);
            return {
                success: false,
                error: error.message || "Failed to refresh preview images",
                data: null,
            };
        }
    },

    /**
     * POST /v1/puzzle/jigsaw/gen-pre-image/refresh/async — starts a background refresh and returns job status.
     * Rendering outlives the gateway timeout, so the sweep runs server-side and progress is polled instead.
     */
    genPreImageRefreshAsync: async (size = 10, { runAll = false, state, language, afterStoryId } = {}) => {
        try {
            const params = new URLSearchParams();
            if (runAll) {
                params.set("all", "true");
                params.set("processAll", "true");
                params.set("size", "all");
            } else {
                params.set("size", String(clampEnsureBatchSize(size)));
            }
            const stateTrim = state != null ? String(state).trim() : "";
            if (stateTrim) params.set("state", stateTrim);
            const langTrim = language != null ? String(language).trim() : "";
            if (langTrim) params.set("language", langTrim);
            const afterStoryIdTrim = afterStoryId != null ? String(afterStoryId).trim() : "";
            if (afterStoryIdTrim) params.set("afterStoryId", afterStoryIdTrim);
            const response = await ApiService.postData(
                `${BASE}/gen-pre-image/refresh/async?${params.toString()}`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to start refresh job", data: null };
            }
            const root = response?.data && typeof response.data === "object" ? response.data : response;
            return { success: true, data: root };
        } catch (error) {
            Logger.error("AdminJigsawService.genPreImageRefreshAsync:", error);
            return { success: false, error: error.message || "Failed to start refresh job", data: null };
        }
    },

    /** GET /v1/puzzle/jigsaw/gen-pre-image/refresh/job — omit jobId for the latest jigsaw job. */
    getPreImageRefreshJob: async (jobId) => {
        try {
            const trimmed = jobId != null ? String(jobId).trim() : "";
            const response = await ApiService.getData(
                `${BASE}/gen-pre-image/refresh/job`,
                trimmed ? { jobId: trimmed } : null
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "No refresh job found", data: null };
            }
            const root = response?.data && typeof response.data === "object" ? response.data : response;
            return { success: true, data: root };
        } catch (error) {
            Logger.error("AdminJigsawService.getPreImageRefreshJob:", error);
            return { success: false, error: error.message || "Failed to read refresh job", data: null };
        }
    },

    /** POST /v1/puzzle/jigsaw/gen-pre-image/refresh/job/{jobId}/cancel — stops after the story in flight. */
    cancelPreImageRefreshJob: async (jobId) => {
        try {
            const trimmed = jobId != null ? String(jobId).trim() : "";
            if (!trimmed) {
                return { success: false, error: "Job id required", data: null };
            }
            const response = await ApiService.postData(
                `${BASE}/gen-pre-image/refresh/job/${encodeURIComponent(trimmed)}/cancel`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Failed to cancel refresh job", data: null };
            }
            const root = response?.data && typeof response.data === "object" ? response.data : response;
            return { success: true, data: root };
        } catch (error) {
            Logger.error("AdminJigsawService.cancelPreImageRefreshJob:", error);
            return { success: false, error: error.message || "Failed to cancel refresh job", data: null };
        }
    },

    /** POST /v1/puzzle/jigsaw/{storyId}/ensure — single-story backfill */
    ensureStoryTemplates: async (storyId) => {
        try {
            if (!storyId || typeof storyId !== "string" || !storyId.trim()) {
                return { success: false, error: "Story id required", data: null };
            }
            const response = await ApiService.postData(
                `${BASE}/${encodeURIComponent(storyId.trim())}/ensure`,
                {}
            );
            if (response?.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Ensure failed",
                    data: null,
                };
            }
            const templatesCreated =
                response?.templatesCreated ??
                response?.data?.templatesCreated ??
                0;
            return {
                success: true,
                data: {
                    storyId: response?.storyId ?? response?.data?.storyId ?? storyId.trim(),
                    templatesCreated: Number(templatesCreated) || 0,
                    jigsawIds: response?.jigsawIds ?? response?.data?.jigsawIds ?? [],
                    raw: response,
                },
            };
        } catch (error) {
            Logger.error("AdminJigsawService.ensureStoryTemplates:", error);
            return {
                success: false,
                error: error.message || "Failed to ensure jigsaw templates",
                data: null,
            };
        }
    },
};

export default AdminJigsawService;
